import { GridColDef } from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import React from "react";
import Typography from "@mui/material/Typography";
import { formatCurrency, formatSignedCurrency } from "common/format";
import { Theme } from "@mui/material/styles";
import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";
import { getAdjustmentReferenceAmount, isAdjustment } from "./adjustments";

export const MONTHLY_DATE_COLUMN: GridColDef = {
  field: "date",
  headerName: "Date",
  type: "date",
  flex: 1,
  sortingOrder: ["desc", "asc"],
};

export const MONTHLY_TOTAL_AMOUNT_COLUMN = (withChevron?: boolean): GridColDef => ({
  field: "amount",
  headerName: "Total Amount",
  type: "number",
  flex: 1,
  align: "left",
  headerAlign: "left",
  sortable: false,
  valueFormatter: ({ value }) => formatCurrency(+value),
  renderCell: (params) => (
    <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} sx={{ width: "100%" }}>
      <Typography
        variant={"body1"}
        sx={{ color: (theme: Theme) => (params.value < 0 ? theme.palette.error.main : undefined) }}
      >
        {`${params.value < 0 ? "-" : ""}$${Math.abs(params.value / 100).toFixed(2)}`}
      </Typography>
      {withChevron && <ArrowForwardIosIcon sx={{ color: (theme: Theme) => theme.palette.action.active }} />}
    </Box>
  ),
});

export const DAILY_TRANSACTION_ID_COLUMN: GridColDef = {
  field: "transactionIndex",
  headerName: "Transaction ID",
  type: "string",
  flex: 1,
  valueGetter({ row }) {
    return row.transactionIndex ? row.transactionIndex.toString().padStart(6, "0") : null;
  },
  sortingOrder: ["desc", "asc"],
};

/**
 * The Amount column. Charges keep their long-standing unsigned base (`|price| - |fee|`, AUT-6147).
 *
 * Adjustment rows (AUT-6163) instead show the signed change to the charge they reference
 * (getAdjustmentReferenceAmount), so a chargeback reads as a red negative reversal rather than as
 * another positive charge. Adjustments carry no service fee: fees are never adjusted.
 */
export const DAILY_TRANSACTION_AMOUNT_COLUMN: GridColDef<CurbsuiteTransactionLogEntry, number, string> = {
  field: "amount",
  headerName: "Amount",
  type: "number",
  flex: 1,
  width: 72,
  align: "left",
  headerAlign: "left",
  sortable: false,
  valueGetter: ({ row }) =>
    isAdjustment(row) ? getAdjustmentReferenceAmount(row) : Math.abs(row.price) - Math.abs(row.serviceFeeAmount),
  valueFormatter: ({ value }) => formatSignedCurrency(+value),
  // Same signed treatment as MONTHLY_TOTAL_AMOUNT_COLUMN: a negative (a reversal) renders in red.
  renderCell: (params) => (
    <Box
      component={"span"}
      sx={{
        color: (theme: Theme) =>
          params.value !== undefined && params.value < 0 ? theme.palette.error.main : undefined,
      }}
    >
      {params.formattedValue}
    </Box>
  ),
  filterable: false,
} as GridColDef<CurbsuiteTransactionLogEntry, number, string>;

/**
 * The card-payment service fee charged on top of the base amount (AUT-6147). `price` carries the
 * full amount charged to the payment method (fee included) and the Amount column subtracts the fee
 * back out, so this column is the only place the fee itself is surfaced.
 *
 * Fee-free rows (ACH/eCheck, wallet-funded, park-only rows with no gateway charge) render $0.00
 * rather than an em dash or blank, so the exported CSV stays summable in Excel.
 */
export const DAILY_TRANSACTION_SERVICE_FEE_COLUMN: GridColDef<CurbsuiteTransactionLogEntry, number, string> = {
  field: "serviceFeeAmount",
  headerName: "Service Fee",
  type: "number",
  flex: 1,
  width: 72,
  align: "left",
  headerAlign: "left",
  sortable: false,
  // `?? 0` guards park-only rows where the view can surface the fee as null at runtime. Adjustments
  // are always $0.00 here: the fee is never adjusted (AUT-6163).
  valueGetter: ({ row }) => (isAdjustment(row) ? 0 : Math.abs(row.serviceFeeAmount ?? 0)),
  valueFormatter: ({ value }) => `$${formatCurrency(+value)}`,
  filterable: false,
} as GridColDef<CurbsuiteTransactionLogEntry, number, string>;

const EM_DASH = "—";

// City Bus / Rural Bus fees are fixed by rate basis rather than read off
// tripFeeAmount, so the displayed figure always matches the known published
// rate for that permit type.
const RATE_BASIS_FEE_CENTS: Record<string, number> = {
  "City Bus": 4000,
  "Rural Bus": 1600,
};

/**
 * "<rate basis> ($<fee>)" for the operator-billing export.
 *
 * Two clients want this column and they disagree on where the money comes from:
 *
 * - `fixedRateBasis` (bus stations): the fee is the *published* rate for the permit type, looked up
 *   in RATE_BASIS_FEE_CENTS and deliberately NOT read off the row, so the figure always matches the
 *   published schedule even if a session was priced oddly. A rate basis outside the map renders an
 *   em dash rather than $NaN.
 * - `rowAmount` (airports): there is no fixed schedule to match -- the fee is per vehicle class and
 *   already carried on the row as trip_fee_amount, priced dynamically at read time from the
 *   vehicle's class. Reading the row is the only correct source, and the map would em-dash every
 *   airport row since none of its labels ("Passenger (16+) (Bus)", …) appear in it.
 */
export function buildTripFeeAmountColumn({
  feeSource,
  headerName,
}: {
  feeSource: "fixedRateBasis" | "rowAmount";
  headerName: string;
}): GridColDef<CurbsuiteTransactionLogEntry, string | null, string> {
  return {
    field: "tripFeeAmount",
    headerName,
    type: "string",
    flex: 1,
    width: 160,
    align: "left",
    headerAlign: "left",
    sortable: false,
    // A no-fee session (voided, pending assignment, or a genuinely unpermitted
    // vehicle in a permit-gated zone) has nothing to show at all -- em dash.
    valueGetter: ({ row }) => {
      if (row.tripFeeAmount === null || row.tripFeeAmount === undefined || !row.rateBasis) {
        return null;
      }
      const feeCents =
        feeSource === "rowAmount" ? Math.abs(Number(row.tripFeeAmount)) : RATE_BASIS_FEE_CENTS[row.rateBasis];
      if (feeCents === undefined || Number.isNaN(feeCents)) {
        return null;
      }
      return `${row.rateBasis} ($${formatCurrency(feeCents)})`;
    },
    valueFormatter: ({ value }) => (value === null || value === undefined ? EM_DASH : value),
    filterable: false,
  } as GridColDef<CurbsuiteTransactionLogEntry, string | null, string>;
}

/** Bus stations (Philly): fee from the published rate schedule. */
export const TRIP_FEE_AMOUNT_COLUMN = buildTripFeeAmountColumn({
  feeSource: "fixedRateBasis",
  headerName: "Permit Type",
});

/** Airports (GSP): fee from the row, labelled by vehicle class rather than permit type. */
export const AIRPORT_TRIP_FEE_AMOUNT_COLUMN = buildTripFeeAmountColumn({
  feeSource: "rowAmount",
  headerName: "Vehicle Type",
});
